import React from 'react';
import { Box, Typography } from '@mui/material';
import { MoChip, MoLinkProps, MoSpacing } from '@motivo/guanyin/src/components';
import { ResultOf } from ':src/graphql';
import { entitiesByEmailsQuery } from '../graphql/queries';
import UserDetails from './user/UserDetails';
import OrganizationAdminDetails from './organizationAdmin/OrganizationAdminDetails';
import ZeusUserLink from './shared/ZeusUserLink';
import ZeusOrganizationAdminLink from './shared/ZeusOrganizationAdminLink';
import ZeusLink from './shared/ZeusLink';

export type Entity = ResultOf<typeof entitiesByEmailsQuery>['entitiesByEmails'][number];

interface EntityNameProps {
  entity: Entity;
}

function EntityName({ entity }: EntityNameProps) {
  const linkProps = { variant: 'h3', fontWeight: 600 } satisfies Partial<MoLinkProps>;
  if (entity.__typename === 'User') {
    if (entity.supervisorApplication?.id && !entity.passwordSetAt) {
      return (
        <ZeusLink
          path={`/supervisor-applications/${entity.supervisorApplication.id}`}
          {...linkProps}
        >
          {entity.fullName}
        </ZeusLink>
      );
    }

    return <ZeusUserLink user={entity} {...linkProps} />;
  }

  if (entity.__typename === 'OrganizationAdmin') {
    return <ZeusOrganizationAdminLink organizationAdmin={entity} {...linkProps} />;
  }

  return (
    <Typography variant="h3" fontWeight={600}>
      {entity.fullName}
    </Typography>
  );
}

interface EntityDetailsProps {
  entity: Entity;
}

export default function EntityDetails({ entity }: EntityDetailsProps) {
  const isUser = entity.__typename === 'User';
  const isOrganizationAdmin = entity.__typename === 'OrganizationAdmin';

  const isOnWaitlist = isUser && entity.supervisorApplication?.id && !entity.passwordSetAt;

  return (
    <MoSpacing y={3}>
      <Box>
        <MoSpacing y={0.5}>
          <EntityName entity={entity} />
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {isUser && (entity.isSupervisor || isOnWaitlist) && (
              <MoChip
                label={isOnWaitlist ? 'On waitlist' : 'Supervisor'}
                color="roseLight"
                size="sm"
              />
            )}
            {isUser && entity.isSupervisee && (
              <MoChip label="Supervisee" color="yellow" size="sm" />
            )}
            {isOrganizationAdmin && (
              <MoChip label="Employer admin" color="bluebellLight" size="sm" />
            )}

            {isUser && entity.hasOpenIncidents && (
              <MoChip label="Has open incident" color="red" size="sm" />
            )}
          </Box>
        </MoSpacing>
      </Box>

      <Box>
        {isUser && <UserDetails userId={entity.id} />}
        {isOrganizationAdmin && <OrganizationAdminDetails organizationAdminId={entity.id} />}
      </Box>
    </MoSpacing>
  );
}
